import React, { useState, useEffect } from 'react';
import { Plus, MessageSquare, Copy, Trash2, FileText, CheckCircle, Loader2, Linkedin, Twitter, Image, Film, X, User, Save, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Edit, Eye, EyeOff, LayoutTemplate, Clock, Send, CalendarDays } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MediaUploader from '../../components/MediaUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_AVATAR = "https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/dnctg08g_1780602847089.jpg";

/* ── Live Post Preview Card ── */
const PostPreview = ({ form, profile }) => {
  const authorImg = profile?.social_avatar || profile?.profile_image || FALLBACK_AVATAR;
  const authorName = profile?.name ? `Dr ${profile.name}` : 'Dr Promise Monday';
  const platform = form.platforms?.includes('linkedin') ? 'LinkedIn' : 'X';
  const hasContent = form.content?.trim();

  return (
    <div className="rounded-xl overflow-hidden border border-gold/[0.12]" data-testid="post-preview-card">
      {/* Preview label */}
      <div className="bg-[#0A2040] border-b border-cream/[0.06] px-4 py-2 flex items-center justify-between">
        <span className="text-[9px] uppercase tracking-[0.25em] text-cream/25 font-sans">Live Preview</span>
        <span className="text-[9px] text-cream/20 font-sans">{platform}</span>
      </div>

      {/* Card body — mirrors SocialPage post card */}
      <div className="bg-white p-4">
        {/* Author row */}
        <div className="flex items-center gap-3 mb-3">
          <img src={authorImg} alt="" className="w-10 h-10 rounded-full object-cover" style={{ objectPosition: 'center 15%' }} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-navy/80 text-sm font-semibold">{authorName}</span>
              <CheckCircle size={12} className="text-blue-400" fill="rgb(96,165,250)" stroke="rgb(59,130,246)" strokeWidth={2} />
            </div>
            <p className="text-navy/25 text-[10px]">{platform} &bull; Just now</p>
          </div>
        </div>

        {/* Content */}
        {hasContent ? (
          <p className="text-navy/60 text-sm leading-relaxed whitespace-pre-line mb-3">{form.content}</p>
        ) : (
          <p className="text-navy/20 text-sm italic mb-3">Your post content will appear here…</p>
        )}

        {/* Media */}
        {form.media_urls?.length > 0 && (
          <div className={`mb-3 rounded-lg overflow-hidden ${form.media_urls.length === 1 ? '' : 'grid grid-cols-2 gap-1'}`}>
            {form.media_urls.map((url, i) =>
              url.match(/\.(mp4|mov|avi|webm)/) ? (
                <div key={i} className="w-full h-28 bg-navy/[0.06] flex items-center justify-center rounded-lg">
                  <Film size={20} className="text-navy/25" />
                </div>
              ) : (
                <img key={i} src={url} alt="" className="w-full object-cover rounded-lg max-h-48" />
              )
            )}
          </div>
        )}

        {/* Action bar */}
        <div className="flex items-center gap-1 pt-2 border-t border-navy/[0.05]">
          {[{ label: 'Like' }, { label: 'Comment' }, { label: 'Repost' }, { label: 'Share' }].map((a) => (
            <button key={a.label} disabled className="flex-1 py-2 text-navy/20 text-xs text-center rounded-lg">{a.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Schedule Queue: Timeline + 7-day Calendar ── */
const ScheduleQueue = ({ posts, onPublishNow, onEdit, publishingId }) => {
  const [open, setOpen]           = useState(true);
  const [viewMode, setViewMode]   = useState('calendar'); // 'calendar' | 'timeline'
  const [weekOffset, setWeekOffset] = useState(0);
  const [activePill, setActivePill] = useState(null); // post.id for popover

  const scheduled = [...posts.filter(p => p.status === 'scheduled')]
    .sort((a, b) => new Date(a.schedule_at || 0) - new Date(b.schedule_at || 0));

  if (scheduled.length === 0) return null;

  const now        = new Date();
  const todayStr   = now.toISOString().slice(0, 10);

  /* ── helpers ── */
  const relativeCountdown = (dateStr) => {
    if (!dateStr) return '';
    const diff = new Date(dateStr) - now;
    if (diff < 0) {
      const hrs = Math.abs(Math.round(diff / 3600000));
      return hrs < 24 ? `${hrs}h overdue` : `${Math.round(hrs / 24)}d overdue`;
    }
    const mins = Math.round(diff / 60000);
    if (mins < 60) return `in ${mins}m`;
    const hrs  = Math.round(diff / 3600000);
    if (hrs < 24) return `in ${hrs}h`;
    return `in ${Math.round(hrs / 24)}d`;
  };

  /* ── Calendar helpers ── */
  // Monday of the current week + weekOffset weeks
  const getMondayOfWeek = (offset = 0) => {
    const d = new Date(now);
    const day = d.getDay() === 0 ? 6 : d.getDay() - 1; // Mon=0
    d.setDate(d.getDate() - day + offset * 7);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const monday = getMondayOfWeek(weekOffset);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return d;
  });

  const weekLabel = () => {
    const start = weekDays[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const end   = weekDays[6].toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${start} – ${end}`;
  };

  // Posts mapped to ISO date strings
  const postsByDay = scheduled.reduce((acc, p) => {
    const key = p.schedule_at ? p.schedule_at.slice(0, 10) : null;
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  /* ── Timeline mode ── */
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const relativeDayLabel = (dateStr) => {
    if (!dateStr) return 'Unscheduled';
    const d    = new Date(dateStr);
    const diff = Math.floor((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - startOfToday) / 86400000);
    if (diff < 0)  return 'OVERDUE';
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const grouped = scheduled.reduce((acc, p) => {
    const label = relativeDayLabel(p.schedule_at);
    if (!acc[label]) acc[label] = [];
    acc[label].push(p);
    return acc;
  }, {});

  return (
    <div className="mb-6 bg-navy border border-gold/[0.06] rounded-xl overflow-hidden" data-testid="schedule-queue">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gold/[0.04]">
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
          data-testid="queue-toggle"
        >
          <CalendarDays size={13} className="text-amber-400/70" />
          <span className="text-cream/50 text-xs font-medium">Schedule Queue</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/15">
            {scheduled.length} post{scheduled.length !== 1 ? 's' : ''}
          </span>
        </button>

        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex rounded-lg border border-gold/[0.06] overflow-hidden">
            {[
              { mode: 'calendar', label: 'Cal' },
              { mode: 'timeline', label: 'List' },
            ].map(({ mode, label }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                data-testid={`queue-view-${mode}`}
                className={`px-2.5 py-1 text-[10px] transition-colors ${
                  viewMode === mode
                    ? 'bg-amber-500/10 text-amber-300'
                    : 'text-cream/20 hover:text-cream/50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {open ? <ChevronUp size={13} className="text-cream/20" /> : <ChevronDown size={13} className="text-cream/20" />}
        </div>
      </div>

      {open && (
        <>
          {/* ── CALENDAR VIEW ── */}
          {viewMode === 'calendar' && (
            <div data-testid="queue-calendar">
              {/* Week nav */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gold/[0.03]">
                <button
                  onClick={() => setWeekOffset(w => w - 1)}
                  data-testid="calendar-prev-week"
                  className="p-1.5 rounded-lg text-cream/25 hover:text-cream/60 hover:bg-cream/[0.04] transition-all"
                >
                  <ChevronLeft size={13} />
                </button>
                <span className="text-cream/30 text-[10px]">{weekLabel()}</span>
                <button
                  onClick={() => setWeekOffset(w => w + 1)}
                  data-testid="calendar-next-week"
                  className="p-1.5 rounded-lg text-cream/25 hover:text-cream/60 hover:bg-cream/[0.04] transition-all"
                >
                  <ChevronRight size={13} />
                </button>
              </div>

              {/* Day-name header row */}
              <div className="grid grid-cols-7 border-b border-gold/[0.03]">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                  <div key={d} className="py-1.5 text-center text-[9px] text-cream/15 uppercase tracking-wider">{d}</div>
                ))}
              </div>

              {/* Date cells row */}
              <div className="grid grid-cols-7">
                {weekDays.map((day, idx) => {
                  const iso       = day.toISOString().slice(0, 10);
                  const isToday   = iso === todayStr;
                  const dayPosts  = postsByDay[iso] || [];
                  const isPast    = day < startOfToday;

                  return (
                    <div
                      key={iso}
                      data-testid={`calendar-day-${iso}`}
                      className={`min-h-[80px] p-2 border-r border-b border-gold/[0.03] last:border-r-0 ${
                        idx === 6 ? '' : ''
                      } ${isPast ? 'opacity-50' : ''}`}
                    >
                      {/* Date number */}
                      <div className={`text-[10px] font-medium mb-1.5 w-5 h-5 flex items-center justify-center rounded-full ${
                        isToday ? 'bg-amber-400/20 text-amber-300' : 'text-cream/20'
                      }`}>
                        {day.getDate()}
                      </div>

                      {/* Post pills */}
                      <div className="space-y-1">
                        {dayPosts.map(p => {
                          const isOverdue  = new Date(p.schedule_at) < now;
                          const timeStr    = new Date(p.schedule_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                          const isActive   = activePill === p.id;

                          return (
                            <div key={p.id} className="relative">
                              <button
                                onClick={() => setActivePill(isActive ? null : p.id)}
                                data-testid={`calendar-pill-${p.id}`}
                                className={`w-full text-left px-1.5 py-1 rounded text-[9px] leading-tight transition-all ${
                                  isOverdue
                                    ? 'bg-red-500/10 text-red-300 hover:bg-red-500/15'
                                    : 'bg-amber-500/10 text-amber-200 hover:bg-amber-500/15'
                                }`}
                              >
                                <span className="font-mono">{timeStr}</span>
                                <span className="text-[8px] ml-1 opacity-60 block truncate">{p.content?.slice(0, 30)}</span>
                              </button>

                              {/* Popover actions */}
                              {isActive && (
                                <div className="absolute z-20 left-0 top-full mt-1 w-40 bg-[#06111f] border border-gold/20 rounded-xl p-2 shadow-2xl" data-testid={`calendar-popover-${p.id}`}>
                                  <p className="text-cream/40 text-[9px] line-clamp-3 mb-2 leading-relaxed">{p.content?.slice(0, 80)}</p>
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => { onPublishNow(p); setActivePill(null); }}
                                      disabled={publishingId === p.id}
                                      data-testid={`queue-publish-now-${p.id}`}
                                      className="flex-1 py-1 text-[9px] rounded bg-green-500/10 text-green-400 hover:bg-green-500/15 transition-all disabled:opacity-50"
                                    >
                                      {publishingId === p.id ? '…' : 'Publish'}
                                    </button>
                                    <button
                                      onClick={() => { onEdit(p); setActivePill(null); }}
                                      data-testid={`queue-edit-${p.id}`}
                                      className="flex-1 py-1 text-[9px] rounded bg-cream/[0.04] text-cream/30 hover:text-gold transition-all"
                                    >
                                      Edit
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Empty-week hint */}
              {weekDays.every(d => !(postsByDay[d.toISOString().slice(0, 10)]?.length)) && (
                <div className="py-5 text-center text-cream/15 text-xs border-t border-gold/[0.03]">
                  No posts scheduled this week — use the form below to add some
                </div>
              )}
            </div>
          )}

          {/* ── TIMELINE VIEW ── */}
          {viewMode === 'timeline' && (
            <div className="divide-y divide-gold/[0.03]" data-testid="queue-timeline">
              {Object.entries(grouped).map(([dayLabel, dayPosts]) => (
                <div key={dayLabel}>
                  <div className="flex items-center gap-3 px-5 py-2 bg-cream/[0.01]">
                    <span className={`text-[10px] font-medium uppercase tracking-wider ${
                      dayLabel === 'OVERDUE' ? 'text-red-400' :
                      dayLabel === 'Today'   ? 'text-amber-300' :
                      dayLabel === 'Tomorrow' ? 'text-cream/50' : 'text-cream/25'
                    }`}>{dayLabel}</span>
                    <div className="flex-1 h-px bg-gold/[0.04]" />
                  </div>
                  {dayPosts.map(p => {
                    const isOverdue = new Date(p.schedule_at) < now;
                    const timeStr   = p.schedule_at
                      ? new Date(p.schedule_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                      : '—';
                    return (
                      <div key={p.id} className="flex items-start gap-4 px-5 py-3 hover:bg-cream/[0.01] transition-colors" data-testid={`queue-item-${p.id}`}>
                        <div className="flex-shrink-0 w-14 text-center">
                          <span className="text-[11px] tabular-nums text-cream/30 font-mono">{timeStr}</span>
                        </div>
                        <div className="flex-shrink-0 flex flex-col items-center pt-1">
                          <div className={`w-2 h-2 rounded-full border ${isOverdue ? 'bg-red-500/40 border-red-500/40' : 'bg-amber-400/30 border-amber-400/40'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-cream/50 text-xs leading-relaxed line-clamp-2">{p.content}</p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {p.platforms?.map(pl => <span key={pl} className="text-[9px] text-cream/20 bg-cream/[0.03] px-1.5 py-0.5 rounded">{pl}</span>)}
                            <span className={`text-[9px] font-medium ${isOverdue ? 'text-red-400/70' : 'text-amber-300/50'}`}>{relativeCountdown(p.schedule_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button onClick={() => onPublishNow(p)} disabled={publishingId === p.id} data-testid={`queue-publish-now-${p.id}`} className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded transition-all disabled:opacity-50 ${isOverdue ? 'bg-red-500/10 text-red-400 hover:bg-red-500/15' : 'bg-green-500/10 text-green-400 hover:bg-green-500/15'}`}>
                            {publishingId === p.id ? <Loader2 size={9} className="animate-spin" /> : <Send size={9} />}
                            Publish Now
                          </button>
                          <button onClick={() => onEdit(p)} data-testid={`queue-edit-${p.id}`} className="px-2 py-1 text-[10px] rounded bg-cream/[0.03] text-cream/25 hover:text-gold transition-all">
                            Edit
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const AdminSocial = () => {
  const [posts, setPosts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showArticleGen, setShowArticleGen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [publishingId, setPublishingId] = useState(null);
  const [form, setForm] = useState({ post_type: 'short', content: '', thread_posts: [], platforms: ['linkedin', 'twitter'], status: 'draft', schedule_at: '', media_urls: [] });
  const [profile, setProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  const { token } = useAuth();

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/bio-profile`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setProfile(await res.json());
    } catch (e) { console.error(e); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/bio-profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(profile),
      });
      if (res.ok) { setProfile(await res.json()); setProfileSaved(true); setTimeout(() => setProfileSaved(false), 2000); }
    } catch (e) { console.error(e); }
    setSavingProfile(false);
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/social`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setPosts(d.posts); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchArticles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/articles?status=published&limit=20`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setArticles(d.articles); }
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchPosts(); fetchProfile(); }, []);

  const handleSave = async () => {
    await handleSaveEdit();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    await fetch(`${API_URL}/api/cms/social/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchPosts();
  };

  const togglePublish = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    await fetch(`${API_URL}/api/cms/social/${post.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...post, status: newStatus }),
    });
    fetchPosts();
  };

  const publishNow = async (post) => {
    setPublishingId(post.id);
    await fetch(`${API_URL}/api/cms/social/${post.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...post, status: 'published', schedule_at: null }),
    });
    await fetchPosts();
    setPublishingId(null);
  };

  const startEdit = (post) => {
    setForm({ ...post, media_urls: post.media_urls || [] });
    setEditingId(post.id);
    setShowForm(true);
  };

  const handleSaveEdit = async () => {
    if (editingId) {
      await fetch(`${API_URL}/api/cms/social/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
    } else {
      await fetch(`${API_URL}/api/cms/social`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(form) });
    }
    setShowForm(false);
    setEditingId(null);
    setForm({ post_type: 'short', content: '', thread_posts: [], platforms: ['linkedin', 'twitter'], status: 'draft', schedule_at: '', media_urls: [] });
    fetchPosts();
  };

  const generateFromArticle = async (articleId) => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/social/from-article/${articleId}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { setShowArticleGen(false); fetchPosts(); }
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const copyForPlatform = (content, platform) => {
    let formatted = content;
    if (platform === 'linkedin') {
      formatted = content.replace(/#(\w+)/g, '#$1');
    } else {
      // Trim for X (280 chars)
      if (formatted.length > 280) formatted = formatted.slice(0, 277) + '...';
    }
    navigator.clipboard.writeText(formatted);
    setCopied(platform);
    setTimeout(() => setCopied(''), 2000);
  };

  const inputClass = "w-full bg-navy border border-cream/10 rounded-lg px-3 py-2 text-sm text-cream placeholder-cream/20 focus:outline-none focus:ring-1 focus:ring-gold/30";

  return (
    <div data-testid="admin-social">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-serif text-2xl text-cream font-semibold">Social Hub</h1><p className="text-cream/30 text-sm mt-1">Compose, schedule, export</p></div>
        <div className="flex gap-2">
          <button onClick={() => { setShowProfile(!showProfile); if (!profile) fetchProfile(); }} className="inline-flex items-center gap-1.5 px-3 py-2 border border-cream/10 text-cream/50 text-xs rounded-lg hover:border-gold/20 hover:text-gold" data-testid="edit-profile-btn">
            <User size={14} /> Edit Profile {showProfile ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          <button onClick={() => { setShowArticleGen(true); fetchArticles(); }} className="inline-flex items-center gap-1.5 px-3 py-2 border border-cream/10 text-cream/50 text-xs rounded-lg hover:border-gold/20 hover:text-gold" data-testid="gen-from-article-btn">
            <FileText size={14} /> From Article
          </button>
          <button onClick={() => { setShowForm(true); setEditingId(null); setForm({ post_type: 'short', content: '', thread_posts: [], platforms: ['linkedin', 'twitter'], status: 'draft', schedule_at: '', media_urls: [] }); }} className="inline-flex items-center gap-1.5 px-4 py-2 bg-gold text-navy text-xs font-semibold rounded-lg hover:bg-gold-light" data-testid="new-post-btn">
            <Plus size={14} /> New Post
          </button>
        </div>
      </div>

      {/* Social Profile Editor */}
      {showProfile && profile && (
        <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 mb-6" data-testid="social-profile-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-cream text-sm font-semibold">Social Profile</h2>
            <button onClick={saveProfile} disabled={savingProfile} className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-gold text-navy text-xs font-semibold rounded-lg hover:bg-gold-light disabled:opacity-50" data-testid="save-profile-btn">
              {savingProfile ? <Loader2 size={12} className="animate-spin" /> : profileSaved ? <><CheckCircle size={12} /> Saved</> : <><Save size={12} /> Save Profile</>}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Avatar */}
            <div>
              <label className="block text-[10px] text-cream/20 mb-1.5 uppercase tracking-wider">Profile Image</label>
              {(profile.social_avatar || profile.profile_image) && (
                <div className="mb-2 flex justify-center">
                  <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-gold/30 bg-navy-deep flex-shrink-0">
                    <img src={profile.social_avatar || profile.profile_image} alt="Avatar" className="w-full h-full object-cover object-[center_15%]" style={{objectPosition: 'center 15%'}} />
                  </div>
                </div>
              )}
              <MediaUploader
                accept="image"
                folder="social"
                label="Upload avatar"
                onUpload={(url) => setProfile(p => ({ ...p, social_avatar: url }))}
              />
              <input value={profile.social_avatar || ''} onChange={(e) => setProfile({ ...profile, social_avatar: e.target.value })} placeholder="Or paste URL..." className={`${inputClass} mt-1.5 text-xs`} data-testid="social-avatar-url" />
            </div>

            {/* Info */}
            <div className="md:col-span-2 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Display Name</label>
                  <input value={profile.name || ''} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={inputClass} data-testid="social-name-input" />
                </div>
                <div>
                  <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Title / Role</label>
                  <input value={profile.social_title || ''} onChange={(e) => setProfile({ ...profile, social_title: e.target.value })} placeholder="Paediatrician • Author • Founder, Third Culture Leadership" className={inputClass} data-testid="social-title-edit" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Bio</label>
                <textarea value={profile.social_bio || ''} onChange={(e) => setProfile({ ...profile, social_bio: e.target.value })} rows={3} placeholder="Your social bio..." className={`${inputClass} resize-none`} data-testid="social-bio-edit" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Followers</label>
                  <input value={profile.social_followers || ''} onChange={(e) => setProfile({ ...profile, social_followers: e.target.value })} placeholder="4.8K" className={inputClass} data-testid="social-followers-edit" />
                </div>
                <div>
                  <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Connections</label>
                  <input value={profile.social_connections || ''} onChange={(e) => setProfile({ ...profile, social_connections: e.target.value })} placeholder="500+" className={inputClass} data-testid="social-connections-edit" />
                </div>
                <div>
                  <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Website</label>
                  <input value={profile.social_website || ''} onChange={(e) => setProfile({ ...profile, social_website: e.target.value })} placeholder="yoursite.com" className={inputClass} data-testid="social-website-edit" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate from Article */}
      {showArticleGen && (
        <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 mb-6" data-testid="article-gen-panel">
          <h2 className="text-cream text-sm font-semibold mb-3">Generate Social Posts from Article</h2>
          <p className="text-cream/25 text-xs mb-4">Creates LinkedIn + X drafts automatically from article content.</p>
          {articles.length === 0 ? <p className="text-cream/20 text-xs">No published articles found.</p> : (
            <div className="space-y-2">
              {articles.map((a) => (
                <div key={a.id} className="flex items-center justify-between bg-navy-deep rounded-lg px-4 py-3">
                  <span className="text-cream/50 text-sm truncate flex-1">{a.title}</span>
                  <button onClick={() => generateFromArticle(a.id)} disabled={generating} className="px-3 py-1 bg-gold text-navy text-xs font-semibold rounded hover:bg-gold-light disabled:opacity-40 ml-3">
                    {generating ? <Loader2 size={12} className="animate-spin" /> : 'Generate'}
                  </button>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => setShowArticleGen(false)} className="mt-3 text-cream/20 text-xs hover:text-cream/40">Cancel</button>
        </div>
      )}

      {/* Compose Form */}
      {showForm && (
        <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 mb-6">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Type</label>
              <select value={form.post_type} onChange={(e) => setForm({ ...form, post_type: e.target.value })} className={inputClass}><option value="short">Short (X-style)</option><option value="long">Long (LinkedIn)</option><option value="thread">Thread</option><option value="carousel">Carousel</option></select></div>
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputClass}><option value="draft">Draft</option><option value="scheduled">Scheduled</option><option value="published">Published</option></select></div>
          </div>
          <div className="mb-4"><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Content</label><textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={5} className={`${inputClass} resize-none`} placeholder="Write your post..." /></div>
          
          {/* Media attachments */}
          <div className="mb-4">
            <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Media Attachments</label>
            {form.media_urls.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {form.media_urls.map((url, i) => (
                  <div key={i} className="relative group">
                    {url.match(/\.(mp4|mov|avi|webm)/) ? (
                      <div className="w-20 h-20 bg-navy-deep rounded-lg flex items-center justify-center border border-cream/[0.06]"><Film size={20} className="text-cream/30" /></div>
                    ) : (
                      <img src={url} alt="" className="w-20 h-20 object-cover rounded-lg border border-cream/[0.06]" />
                    )}
                    <button onClick={() => setForm(prev => ({ ...prev, media_urls: prev.media_urls.filter((_, j) => j !== i) }))} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-media-${i}`}><X size={10} /></button>
                  </div>
                ))}
              </div>
            )}
            <MediaUploader
              accept="all"
              folder="social"
              label="Upload image or video"
              onUpload={(url) => setForm(prev => ({ ...prev, media_urls: [...prev.media_urls, url] }))}
            />
          </div>

          {form.status === 'scheduled' && (
            <div className="mb-4 p-4 bg-amber-500/[0.05] border border-amber-500/20 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <CalendarDays size={13} className="text-amber-400" />
                <label className="text-[10px] text-amber-300/60 uppercase tracking-wider font-medium">Schedule publish date &amp; time</label>
              </div>
              <input
                type="datetime-local"
                value={form.schedule_at || ''}
                onChange={(e) => setForm({ ...form, schedule_at: e.target.value })}
                className={`${inputClass} border-amber-500/20`}
                data-testid="schedule-at-input"
              />
              {form.schedule_at && (
                <p className="mt-2 text-amber-300/40 text-[10px]">
                  Will go live: {new Date(form.schedule_at).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          )}

          {/* Live preview panel */}
          {showPreview && (
            <div className="mb-5" data-testid="live-preview-panel">
              <PostPreview form={form} profile={profile} />
            </div>
          )}

          <div className="flex gap-2 flex-wrap items-center">
            <button onClick={handleSave} className="px-4 py-2 bg-gold text-navy text-xs font-semibold rounded-lg hover:bg-gold-light" data-testid="save-post-btn">{editingId ? 'Update' : 'Save'}</button>
            <button
              onClick={() => setShowPreview(p => !p)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border transition-all ${showPreview ? 'border-gold/30 text-gold bg-gold/[0.06]' : 'border-cream/10 text-cream/40 hover:border-gold/20 hover:text-gold'}`}
              data-testid="toggle-preview-btn"
            >
              <LayoutTemplate size={12} /> {showPreview ? 'Hide Preview' : 'Preview'}
            </button>
            <button onClick={() => { setShowForm(false); setEditingId(null); setShowPreview(false); }} className="px-3 py-2 text-cream/30 text-xs">Cancel</button>
          </div>
        </div>
      )}

      {/* Schedule Queue — timeline of upcoming scheduled posts */}
      <ScheduleQueue
        posts={posts}
        onPublishNow={publishNow}
        onEdit={startEdit}
        publishingId={publishingId}
      />

      {/* Status filter tabs */}
      {!loading && posts.length > 0 && (
        <div className="flex items-center gap-1 mb-4">
          {[
            { key: 'all', label: 'All', count: posts.length },
            { key: 'draft', label: 'Drafts', count: posts.filter(p => p.status === 'draft').length },
            { key: 'scheduled', label: 'Scheduled', count: posts.filter(p => p.status === 'scheduled').length },
            { key: 'published', label: 'Published', count: posts.filter(p => p.status === 'published').length },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              data-testid={`filter-tab-${tab.key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-all ${statusFilter === tab.key ? 'border-gold/30 text-gold bg-gold/[0.06]' : 'border-gold/[0.06] text-cream/30 hover:text-cream/60'}`}
            >
              {tab.label}
              {tab.count > 0 && <span className={`text-[9px] px-1 rounded-full ${statusFilter === tab.key ? 'bg-gold/20 text-gold' : 'bg-cream/[0.06] text-cream/20'}`}>{tab.count}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Posts */}
      {loading ? <div className="py-12 text-center text-cream/20 text-sm">Loading...</div> : posts.length === 0 ? (
        <div className="py-12 text-center bg-navy border border-dashed border-gold/[0.06] rounded-xl"><MessageSquare size={28} className="mx-auto text-cream/10 mb-2" /><p className="text-cream/25 text-sm">No social posts yet</p></div>
      ) : (
        <div className="space-y-2">
          {posts.filter(p => statusFilter === 'all' || p.status === statusFilter).map((p) => (
            <div key={p.id} className="bg-navy border border-gold/[0.06] rounded-xl p-4 hover:border-gold/[0.1] transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-cream/[0.05] text-cream/30">{p.post_type}</span>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${p.status === 'published' ? 'bg-green-500/10 text-green-400' : p.status === 'scheduled' ? 'bg-amber-500/10 text-amber-300' : 'bg-cream/[0.03] text-cream/20'}`}>{p.status}</span>
                    {p.platforms?.map(pl => <span key={pl} className="text-[9px] text-cream/15">{pl}</span>)}
                    {/* Schedule indicator */}
                    {p.status === 'scheduled' && p.schedule_at && (
                      <span className="flex items-center gap-1 text-[9px] text-amber-300/50 bg-amber-500/[0.06] border border-amber-500/15 rounded px-1.5 py-0.5">
                        <Clock size={8} />
                        {new Date(p.schedule_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <p className="text-cream/50 text-sm whitespace-pre-line line-clamp-4">{p.content}</p>
                  {p.media_urls?.length > 0 && (
                    <div className="flex gap-2 mt-2">
                      {p.media_urls.map((url, mi) => (
                        <div key={mi} className="w-16 h-16 rounded overflow-hidden border border-cream/[0.06]">
                          {url.match(/\.(mp4|mov|avi|webm)/) ? (
                            <div className="w-full h-full bg-navy-deep flex items-center justify-center"><Film size={16} className="text-cream/20" /></div>
                          ) : (
                            <img src={url} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  {/* Publish Now — for drafts and scheduled */}
                  {p.status !== 'published' && (
                    <button
                      onClick={() => publishNow(p)}
                      disabled={publishingId === p.id}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-green-500/10 text-green-400 hover:bg-green-500/15 transition-all disabled:opacity-50"
                      title="Publish immediately"
                      data-testid={`publish-now-${p.id}`}
                    >
                      {publishingId === p.id ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                      Publish Now
                    </button>
                  )}
                  {p.status === 'published' && (
                    <button onClick={() => togglePublish(p)} className="flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-green-500/10 text-green-400 hover:bg-red-500/10 hover:text-red-400 transition-all" title="Unpublish" data-testid={`toggle-publish-${p.id}`}>
                      <EyeOff size={10} /> Unpublish
                    </button>
                  )}
                  <button onClick={() => startEdit(p)} className="flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-cream/[0.03] text-cream/25 hover:text-gold transition-all" title="Edit post" data-testid={`edit-post-${p.id}`}>
                    <Edit size={10} /> Edit
                  </button>
                  <button onClick={() => copyForPlatform(p.content, 'linkedin')} className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded transition-all ${copied === 'linkedin' ? 'bg-green-500/10 text-green-400' : 'bg-cream/[0.03] text-cream/25 hover:text-gold'}`} title="Copy for LinkedIn">
                    <Linkedin size={10} /> {copied === 'linkedin' ? 'Copied' : 'LinkedIn'}
                  </button>
                  <button onClick={() => copyForPlatform(p.content, 'twitter')} className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded transition-all ${copied === 'twitter' ? 'bg-green-500/10 text-green-400' : 'bg-cream/[0.03] text-cream/25 hover:text-gold'}`} title="Copy for X">
                    <Twitter size={10} /> {copied === 'twitter' ? 'Copied' : 'X'}
                  </button>
                  <button onClick={() => handleDelete(p.id)} className="p-1 text-cream/15 hover:text-red-400 transition-colors self-center mt-1"><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSocial;
